import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../axiosInstance";
import {
  EmptyState,
  ParentPageHeader,
  PortalLoading,
  StatusBadge,
  niceDate,
  niceDateTime,
  useParentPortal,
} from "./parentPortalUtils";

function ParentChildLearningPage() {
  const { studentId } = useParams();
  const { data, loading, reload } = useParentPortal(
    `parent-portal/children/${studentId}/learning/`
  );
  const [assignments, setAssignments] = useState([]);

  const loadAssignments = async () => {
    try {
      const response = await axiosInstance.get(
        `parent-portal/children/${studentId}/assignment-submissions/`
      );
      setAssignments(response.data?.assignments || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    loadAssignments();
  }, [studentId]);

  if (loading && !data) return <PortalLoading />;

  const resources = data?.resources || [];
  const liveClasses = data?.live_classes || [];

  return (
    <div className="container-fluid py-4">
      <ParentPageHeader
        title="Child Learning"
        subtitle={data?.student?.full_name || "Learning resources, assigned work and submissions"}
        icon="bi-play-circle"
        actions={
          <button
            className="btn btn-light border"
            onClick={() => {
              reload();
              loadAssignments();
            }}
          >
            Refresh
          </button>
        }
      />

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <h5 className="fw-bold mb-3">Homework & Assignment Progress</h5>
          {assignments.length === 0 ? (
            <EmptyState title="No homework or assignments" />
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead className="table-light">
                  <tr><th>Work</th><th>Subject</th><th>Due</th><th>Status</th><th>Teacher Feedback</th></tr>
                </thead>
                <tbody>
                  {assignments.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <strong>{item.title}</strong>
                        <div className="small text-muted">{item.resource_type}</div>
                      </td>
                      <td>{item.subject_name || "General"}</td>
                      <td>{niceDate(item.due_date)}</td>
                      <td>
                        <StatusBadge value={item.submission_status} />
                        {item.overdue && <div><span className="badge text-bg-danger mt-1">OVERDUE</span></div>}
                        {item.submission?.is_late && <div><span className="badge text-bg-warning mt-1">SUBMITTED LATE</span></div>}
                        {item.submission?.submitted_at && (
                          <div className="small text-muted mt-1">
                            {niceDateTime(item.submission.submitted_at)}
                          </div>
                        )}
                      </td>
                      <td>
                        {item.submission?.teacher_feedback || (
                          <span className="text-muted">No feedback yet</span>
                        )}
                        {item.submission?.feedback_file_url && (
                          <div>
                            <a href={item.submission.feedback_file_url} target="_blank" rel="noreferrer">
                              Open feedback file
                            </a>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="row g-3">
        <div className="col-xl-7">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="fw-bold mb-3">Learning Resources</h5>
              {resources.length === 0 ? (
                <EmptyState title="No resources" />
              ) : (
                resources.map((item) => (
                  <div className="border rounded-3 p-3 mb-2" key={item.id}>
                    <div className="d-flex justify-content-between">
                      <h6 className="fw-bold">{item.title}</h6>
                      <StatusBadge value={item.can_view ? item.access_level : "Restricted"} />
                    </div>
                    <small className="text-muted">
                      {item.subject || "General"} · {item.resource_type}
                    </small>
                    <p className="small text-muted mt-2 mb-0">
                      {item.description || item.preview_text}
                    </p>
                    {item.external_url && (
                      <a href={item.external_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-light border mt-2">
                        Open Link
                      </a>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="col-xl-5">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="fw-bold mb-3">Live Classes</h5>
              {liveClasses.length === 0 ? (
                <EmptyState title="No live classes" />
              ) : (
                liveClasses.map((item) => (
                  <div className="border rounded-3 p-3 mb-2" key={item.id}>
                    <h6 className="fw-bold mb-1">{item.title}</h6>
                    <small className="text-muted">
                      {item.subject} · {niceDateTime(item.scheduled_start)}
                    </small>
                    <div className="mt-2">
                      <StatusBadge value={item.status} />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ParentChildLearningPage;
